// Per-environment Sentry settings, read at runtime from the operator-managed
// `ph-spa-config` ConfigMap (one image serves every environment). Every key is
// required: a missing or malformed value is an error, never a fallback.
export interface ISentryEnabledConfig {
  enabled: true;
  dsn: string;
  environment: string;
  // Hosts (window.location.host) that report to Sentry; other sites served by
  // the same SPA, such as the admin portal, stay off.
  hosts: string[];
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
}

export type SentryConfig = ISentryEnabledConfig | { enabled: false };

// A host as window.location.host reports it (lower case, no scheme, path or
// spaces), so a pasted URL fails here instead of silently never matching.
const HOST = /^[a-z0-9.-]+(?::\d+)?$/;

// Sentry drops a malformed DSN without a word outside debug builds.
const DSN = /^https:\/\/[^\s@/]+@[a-z0-9.-]+(?::\d+)?\/\d+$/;

function isTrimmedString(value: unknown): value is string {
  return typeof value === "string" && value !== "" && value === value.trim();
}

function isRate(value: unknown): value is number {
  return typeof value === "number" && value >= 0 && value <= 1;
}

export function parseSentryConfig(raw: unknown): SentryConfig {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    throw new Error("Invalid Sentry config: expected a JSON object");
  }

  const config = raw as Record<string, unknown>;

  if (typeof config.enabled !== "boolean") {
    throw new Error("Invalid Sentry config: enabled must be true or false");
  }

  // A disabled environment needs nothing else, so turning Sentry off never
  // requires inventing a DSN.
  if (!config.enabled) {
    return { enabled: false };
  }

  const {
    dsn,
    environment,
    hosts,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
  } = config;
  const problems: string[] = [];

  if (typeof dsn !== "string" || !DSN.test(dsn)) {
    problems.push("dsn must look like https://<key>@<host>/<project id>");
  }
  if (!isTrimmedString(environment)) {
    problems.push(
      "environment must be a non-empty string without surrounding spaces",
    );
  }
  if (
    !Array.isArray(hosts) ||
    hosts.length === 0 ||
    !hosts.every((host) => {
      return typeof host === "string" && HOST.test(host);
    })
  ) {
    problems.push(
      "hosts must be a non-empty list of host names (host or host:port, no scheme or path)",
    );
  }
  if (!isRate(replaysSessionSampleRate)) {
    problems.push("replaysSessionSampleRate must be a number from 0 to 1");
  }
  if (!isRate(replaysOnErrorSampleRate)) {
    problems.push("replaysOnErrorSampleRate must be a number from 0 to 1");
  }

  if (problems.length > 0) {
    throw new Error(`Invalid Sentry config: ${problems.join("; ")}`);
  }

  // Every value was checked above; the casts only restore the narrowing that
  // collecting all problems before throwing loses.
  return {
    enabled: true,
    dsn: dsn as string,
    environment: environment as string,
    hosts: hosts as string[],
    replaysSessionSampleRate: replaysSessionSampleRate as number,
    replaysOnErrorSampleRate: replaysOnErrorSampleRate as number,
  };
}
